import type { BigNumber } from 'bignumber.js';

import { calculateAssetDecimalPrecision } from '../../../utils/amount';
import { formatNumberByOptions } from '../../formatted-number/utils';

export function formatTokenValue(
  tokenValue: BigNumber,
  usdRate: number | null | undefined,
  decimals: number
) {
  const assetDecimalPrecision = usdRate
    ? calculateAssetDecimalPrecision({
        tokenAmount: tokenValue.toString() ?? '0',
        tokenDecimals: decimals,
        rateUsd: usdRate,
      })
    : (decimals ?? 6);

  const formatResult = formatNumberByOptions(tokenValue, {
    decimals: assetDecimalPrecision,
  });

  return { realNotation: formatResult.realNotation, assetDecimalPrecision };
}

/**
 * Formats a number to have at most 2 decimal places by truncating (not rounding).
 * Handles edge cases like null/undefined values and scientific notation.
 *
 * @param value - The value as number or null/undefined
 * @returns Formatted string or empty string if no value
 *
 * @example
 * keepAtMost2Decimals(123.456789) // "123.45" (truncated, not rounded)
 * keepAtMost2Decimals(123.4)      // "123.4"
 * keepAtMost2Decimals(123)        // "123"
 * keepAtMost2Decimals(0)          // "0"
 * keepAtMost2Decimals(999.999)    // "999.99" (truncated, not rounded to 1000.00)
 */
export function keepAtMost2Decimals(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  const usdString = value.toString();

  // Handle scientific notation (e.g., 1e-7)
  if (usdString.includes('e')) {
    return usdString;
  }

  const decimalIndex = usdString.indexOf('.');

  // If there are more than 2 decimal places, truncate to 2
  if (decimalIndex !== -1 && usdString.length - decimalIndex - 1 > 2) {
    // Truncate to 2 decimal places without rounding
    return usdString.substring(0, decimalIndex + 3);
  }

  // Otherwise keep as is (no decimals or ≤2 decimals)
  return usdString;
}
